import React from 'react';
import styled from 'styled-components';
import { FlowjoCommons, Product } from '@portal/portal-core';

import { FjInput, FjModal, FjButton, FjStyles } from 'components/primitives';

const { newUUID } = FlowjoCommons.StringUtils;

export const PREVIEW = 'preview';
export const LIVE = 'live';

interface CustomFieldKey {
  id: string;
  label: string;
  required?: boolean;
}

interface CustomFieldsFormProps {
  className?: string;
  customFields: CustomFieldKey[];
  mode?: string;
  productName?: string;
  costCenterName?: string;
  closePreview?: () => void;
  costCenterId?: string;
  productAccessId?: string;
  userId?: string;
  inviteId?: string;
  // customFieldsAction is dispatched with two different argument shapes below.
  customFieldsAction: (...args: unknown[]) => void;
  phantom?: boolean;
  parentLocalState?: unknown;
  convertPhantomUserToUserCustomFields?: unknown;
  // A new result object each time a live submit fails; re-enables the form so the user can retry.
  // Its `error` is a status code when the server answered and an Error when it didn't.
  submitFailure?: { error?: unknown; [key: string]: unknown };
}

export const SUBMIT_FAILED_MESSAGE = 'Your information could not be saved. Please try again.';

// A timeout or network error doesn't mean the server failed: it may have accepted the invite
// and saved the values, in which case a retry is rejected.
export const SUBMIT_NO_RESPONSE_MESSAGE =
  "We didn't get a response, so your information may or may not have been saved. " +
  'Please check your license before trying again.';

const submitFailureMessage = ({ error }: { error?: unknown }): string =>
  error instanceof Error ? SUBMIT_NO_RESPONSE_MESSAGE : SUBMIT_FAILED_MESSAGE;

interface CustomFieldsFormState {
  errorMessage: string;
  fields: Record<string, string> | undefined;
  disableButton: boolean;
}

const Label = styled.label`
  color: ${({ theme }) => theme.colors.selectableLightBg};
  font-size: 1em;
  font-weight: 400;
  margin: 0 0 5px 0;
`;

const CustomFieldsWrapper = styled.div`
  padding: 10px 10px 0px 10px;
`;

const CustomFieldsButtonWrapper = styled.div`
  margin-top: 6%;
  margin-left: 81%;
`;

const Header = styled.p`
  margin-bottom: 20px;
`;

const Body = styled.div`
  margin-bottom: auto;
`;

const Input = styled.div`
  min-width: 50%;
`;

const getInitialFieldsState = (
  keys: CustomFieldKey[] | undefined,
): Record<string, string> | undefined => {
  if (keys) {
    const fieldsObj: Record<string, string> = {};
    Object.values(keys).forEach((value) => {
      fieldsObj[value.id] = '';
    });
    return fieldsObj;
  }
  return undefined;
};

class CustomFieldsForm extends React.Component<CustomFieldsFormProps, CustomFieldsFormState> {
  state: CustomFieldsFormState = {
    errorMessage: '',
    fields: getInitialFieldsState(this.props.customFields),
    disableButton: false,
  };

  onValueChange = (id: string, value: string): void => {
    const existingFieldState = this.state.fields;
    const newFieldsObj = { [id]: value };
    const newState = Object.assign(existingFieldState!, newFieldsObj);
    this.setState({ fields: newState });
  };

  componentDidUpdate(prevProps: CustomFieldsFormProps): void {
    if (this.props.submitFailure && this.props.submitFailure !== prevProps.submitFailure) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        disableButton: false,
        errorMessage: submitFailureMessage(this.props.submitFailure),
      });
    }
  }

  // Only the form's onSubmit calls this. Without preventDefault the browser also submits the form
  // natively, reloading the page and dropping the in-flight request (FJCLOUD-4502).
  onSubmit = (evt?: React.SyntheticEvent): void => {
    evt?.preventDefault();
    if (this.props.mode === PREVIEW) {
      this.props.closePreview!();
    } else if (!this.state.disableButton) {
      const customFieldKeyInfo = this.props.customFields;
      const fieldValues = this.state.fields;
      const fields: Array<{ id: string; keyId: unknown; value: string }> = [];
      let renderError = false;

      Object.entries(fieldValues!).forEach(([keyId, value]) => {
        // check if a value is an empty string for a field that is required
        const cleanedValue = value.replace(/\s+/g, '');
        const findKeyObj = customFieldKeyInfo.find((key) => key.id === keyId);
        if (!cleanedValue && findKeyObj && findKeyObj.required) {
          renderError = true;
          this.setState({ errorMessage: 'Provide values for each required field.' });
        } else {
          const obj = {
            id: newUUID(),
            keyId: findKeyObj!.id,
            value,
          };
          fields.push(obj);
        }
      });

      if (!renderError) {
        this.setState({ errorMessage: '', disableButton: true });
        const {
          costCenterId,
          productAccessId,
          userId,
          inviteId,
          customFieldsAction,
          phantom,
          parentLocalState,
          convertPhantomUserToUserCustomFields,
        } = this.props;
        if (phantom) {
          customFieldsAction(fields, parentLocalState, convertPhantomUserToUserCustomFields);
        } else {
          customFieldsAction(inviteId, costCenterId, productAccessId, userId, fields);
        }
      }
    }
  };

  renderSubmitButton = (): React.ReactElement => {
    const customFieldKeyInfo = this.props.customFields;
    const fieldValues = this.state.fields;
    let renderDisabled = false;

    // check if the required fields have values
    Object.entries(fieldValues!).forEach(([keyId, value]) => {
      // check if a value is an empty string for a field that is required
      const cleanedValue = value.replace(/\s+/g, '');
      const findKeyObj = customFieldKeyInfo.find((key) => key.id === keyId);
      if (!cleanedValue && findKeyObj && findKeyObj.required) {
        renderDisabled = true;
      }
    });

    if (this.props.mode === PREVIEW) {
      return (
        <CustomFieldsButtonWrapper>
          <FjButton
            type="submit"
            defaultStyle={{
              color: FjStyles.colors.teal.hex(),
              background: FjStyles.colors.white.hex(),
              borderColor: FjStyles.colors.teal.hex(),
            }}
            hoverStyle={{
              color: FjStyles.colors.white.hex(),
              background: FjStyles.colors.teal.hex(),
              borderColor: FjStyles.colors.teal.hex(),
            }}
          >
            Submit
          </FjButton>
          <FjButton
            buttonType="link"
            onClick={() => {
              this.props.closePreview!();
            }}
          >
            Close Preview
          </FjButton>
        </CustomFieldsButtonWrapper>
      );
    }
    if (renderDisabled || this.state.disableButton) {
      return (
        <div className="custom-fields-button-wrapper">
          <FjButton
            type="submit"
            disabled
          >
            Submit
          </FjButton>
        </div>
      );
    }
    return (
      <CustomFieldsButtonWrapper>
        <FjButton
          type="submit"
          defaultStyle={{
            color: FjStyles.colors.teal.hex(),
            background: FjStyles.colors.white.hex(),
            borderColor: FjStyles.colors.teal.hex(),
          }}
          hoverStyle={{
            color: FjStyles.colors.white.hex(),
            background: FjStyles.colors.teal.hex(),
            borderColor: FjStyles.colors.teal.hex(),
          }}
        >
          Submit
        </FjButton>
      </CustomFieldsButtonWrapper>
    );
  };

  render(): React.ReactElement {
    const productDisplayName = Product.PRODUCT_DISPLAY_NAMES[this.props.productName as string];
    return (
      <FjModal size="xl">
        <CustomFieldsWrapper>
          <h3>
            Product invitation to join the {productDisplayName} site license for:{' '}
            <u>
              <i>{this.props.costCenterName}</i>
            </u>
          </h3>
          <form onSubmit={this.onSubmit} className="display-flex flex-column custom-fields-form">
            <Header>
              Your site administrator requested the following information to join this license.
              Fields marked with an asterisk (*) are required fields.
            </Header>
            <Body className="display-flex flex-wrap">
              {this.props.customFields.map((field, i) => (
                <Input key={field.id}>
                  <Label className="margin-left-med">
                    {field.required && '*'} {field.label}
                  </Label>
                  <FjInput
                    key={field.label + field.id}
                    autoFocus={i === 0}
                    value={(this.state as unknown as Record<string, string | undefined>)[field.id]}
                    onChange={(val) => this.onValueChange(field.id, val)}
                    height={30}
                    className="margin-right-med margin-left-med margin-bottom-med"
                    fontSize="0.875rem"
                  />
                </Input>
              ))}
              <div>
                <p>{this.state.errorMessage}</p>
              </div>
            </Body>
            {this.renderSubmitButton()}
          </form>
        </CustomFieldsWrapper>
      </FjModal>
    );
  }
}

export default styled(CustomFieldsForm)`
  height: 100%;
`;
